import { useMemo, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Banner, Field } from '../components/ui';
import { BrandLockup } from '../components/Brand';

type Mode = 'login' | 'signup' | 'forgot';

function inviteFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('invite')?.toUpperCase() ?? '';
  } catch {
    return '';
  }
}

export function AuthPage() {
  const { setUser, setCommunity } = useAuth();
  const initialInvite = useMemo(() => inviteFromUrl(), []);
  const [mode, setMode] = useState<Mode>(initialInvite ? 'signup' : 'login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    unit: '',
    phone: '',
    inviteCode: initialInvite,
    emailConsent: false,
  });

  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setFieldErrors({});
    setSent(false);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});
    setSent(false);

    try {
      if (mode === 'forgot') {
        await api.forgotPassword(form.email);
        setSent(true);
        return;
      }

      if (mode === 'login') {
        const { user, community } = await api.login(form.email, form.password);
        setUser(user);
        setCommunity(community);
        return;
      }

      if (!form.emailConsent) {
        setError('You must accept email communications to create your account.');
        return;
      }

      const { user, community } = await api.signup({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        unit: form.unit,
        phone: form.phone,
        inviteCode: form.inviteCode,
        emailConsent: true,
      });
      setUser(user);
      setCommunity(community);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(caught.details ?? {});
      } else {
        setError('We could not reach the server. Check your connection and try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const firstError = (key: string) => fieldErrors[key]?.[0];

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <BrandLockup size="md" />
        </div>
        <p className="page-lede">
          HOA One is invitation-only. Sign in with your account, or accept an invitation to join
          your community.
        </p>

        <div className="card">
          {mode !== 'forgot' && (
            <div className="auth-switch" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={mode === 'login' ? 'on' : ''}
                onClick={() => switchMode('login')}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                className={mode === 'signup' ? 'on' : ''}
                onClick={() => switchMode('signup')}
              >
                Accept invitation
              </button>
            </div>
          )}

          {mode === 'forgot' && (
            <>
              <h2 className="page-title" style={{ fontSize: 20, marginBottom: 6 }}>
                Forgot password
              </h2>
              <p className="mut" style={{ marginBottom: 16 }}>
                Enter the email on your account. If we have it, we&apos;ll send a link to choose a
                new password.
              </p>
            </>
          )}

          {error && <Banner kind="error">{error}</Banner>}
          {sent && (
            <Banner kind="ok">
              If that email is on file, we sent a reset link. Check your inbox and spam folder.
            </Banner>
          )}

          <form onSubmit={submit} noValidate>
            {mode === 'signup' && (
              <>
                <Field
                  label="Invitation code"
                  hint="Required. Use the code from your invitation email."
                  error={firstError('inviteCode')}
                >
                  <input
                    value={form.inviteCode}
                    onChange={update('inviteCode')}
                    autoCapitalize="characters"
                    required
                    placeholder="ABCD1234"
                  />
                </Field>
                <div className="row" style={{ gap: 12 }}>
                  <Field label="First name" error={firstError('firstName')}>
                    <input
                      value={form.firstName}
                      onChange={update('firstName')}
                      autoComplete="given-name"
                      required
                    />
                  </Field>
                  <Field label="Last name" error={firstError('lastName')}>
                    <input
                      value={form.lastName}
                      onChange={update('lastName')}
                      autoComplete="family-name"
                      required
                    />
                  </Field>
                </div>
                <Field
                  label="Property address"
                  hint="The address or unit for this community membership."
                  error={firstError('unit')}
                >
                  <input
                    value={form.unit}
                    onChange={update('unit')}
                    autoComplete="street-address"
                    required
                    placeholder="18 Willow Drive"
                  />
                </Field>
              </>
            )}

            <Field label="Email" error={firstError('email')}>
              <input
                type="email"
                value={form.email}
                onChange={update('email')}
                autoComplete="email"
                inputMode="email"
                required
              />
            </Field>

            {mode !== 'forgot' && (
              <Field
                label="Password"
                error={firstError('password')}
                hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
              >
                <input
                  type="password"
                  value={form.password}
                  onChange={update('password')}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  required
                />
              </Field>
            )}

            {mode === 'signup' && (
              <>
                <Field label="Phone" error={firstError('phone')}>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update('phone')}
                    autoComplete="tel"
                    inputMode="tel"
                    required
                  />
                </Field>
                <label className="check" style={{ marginTop: 12, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={form.emailConsent}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, emailConsent: event.target.checked }))
                    }
                    required
                  />
                  <span>
                    I agree to receive essential service emails from HOA One and my community about
                    my account, invitations, and community notices. This is required to activate my
                    account and is not optional marketing consent.
                  </span>
                </label>
              </>
            )}

            <button type="submit" className="btn block" disabled={busy} style={{ marginTop: 8 }}>
              {busy
                ? 'Working…'
                : mode === 'login'
                  ? 'Sign in'
                  : mode === 'signup'
                    ? 'Create account'
                    : 'Send reset link'}
            </button>
          </form>

          {mode === 'login' && (
            <p className="auth-footer">
              <button type="button" onClick={() => switchMode('forgot')}>
                Forgot password?
              </button>
            </p>
          )}
          {mode === 'forgot' && (
            <p className="auth-footer">
              <button type="button" onClick={() => switchMode('login')}>
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
